const UNITS_PER_SALE_PATTERN = /^\d{1,20}(?:\.\d{1,10})?$/;

function safeCount(value) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= 0 ? number : 0;
}

function safeId(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
}

export function normalizeSalesImportProductOptions(response) {
  return (Array.isArray(response?.data) ? response.data : []).map((product) => ({
    id: safeId(product.id),
    name: String(product.name || ""),
    isActive: Boolean(product.isActive),
    costStatus: String(product.costStatus || ""),
    costReviewReasons: Array.isArray(product.costReviewReasons)
      ? product.costReviewReasons
      : [],
  })).filter((product) => product.id !== null);
}

function normalizeDescription(description) {
  const resolution = description?.resolution || {};
  return {
    externalDescription: String(description?.externalDescription || ""),
    normalizedDescription: String(description?.normalizedDescription || ""),
    soldQuantity: String(description?.soldQuantity ?? "0"),
    salesAmount: String(description?.salesAmount ?? "0"),
    resolution: {
      kind: String(resolution.kind || "PENDING"),
      source: String(resolution.source || "PENDING"),
      saleProduct: resolution.saleProduct || null,
      unitsPerSale: resolution.unitsPerSale === null || resolution.unitsPerSale === undefined
        ? null
        : String(resolution.unitsPerSale),
      effectiveQuantity: resolution.effectiveQuantity === null || resolution.effectiveQuantity === undefined
        ? null
        : String(resolution.effectiveQuantity),
      cmv: resolution.cmv ?? null,
      composition: Array.isArray(resolution.composition) ? resolution.composition : [],
    },
    issues: Array.isArray(description?.issues) ? description.issues : [],
  };
}

export function normalizeSalesImportMatching(response = {}) {
  const descriptions = (Array.isArray(response.descriptions) ? response.descriptions : [])
    .map(normalizeDescription);
  return {
    draftId: safeId(response.draftId),
    version: safeId(response.version),
    descriptions,
    summary: {
      totalDescriptions: safeCount(response.summary?.totalDescriptions),
      resolvedDescriptions: safeCount(response.summary?.resolvedDescriptions),
      pendingDescriptions: safeCount(response.summary?.pendingDescriptions),
      blockingIssues: safeCount(response.summary?.blockingIssues),
      canContinue: response.summary?.canContinue === true,
    },
    previewInvalidated: response.previewInvalidated === true,
    regularizationPlanInvalidated: response.regularizationPlanInvalidated === true,
  };
}

export function normalizeSalesImportPreview(response = {}) {
  return {
    draftId: safeId(response.draftId),
    version: safeId(response.version),
    previewRevision: safeId(response.previewRevision),
    calculatedAt: response.calculatedAt || null,
    descriptions: (Array.isArray(response.descriptions) ? response.descriptions : [])
      .map(normalizeDescription),
    consumption: Array.isArray(response.consumption) ? response.consumption : [],
    shortages: (Array.isArray(response.shortages) ? response.shortages : [])
      .map((item) => ({
        ...item,
        itemId: safeId(item.itemId),
        itemType: String(item.itemType || ""),
        name: String(item.name || ""),
        unit: String(item.unit || ""),
        currentStock: String(item.currentStock ?? "0"),
        requiredConsumption: String(item.requiredConsumption ?? "0"),
        regularization: String(item.regularization ?? "0"),
        projectedFinalStock: String(item.projectedFinalStock ?? "0"),
        origins: Array.isArray(item.origins) ? item.origins : [],
      })),
    pointsOfSale: Array.isArray(response.pointsOfSale) ? response.pointsOfSale : [],
    issues: Array.isArray(response.issues) ? response.issues : [],
    summary: {
      totalCmv: response.summary?.totalCmv ?? null,
      totalDescriptions: safeCount(response.summary?.totalDescriptions),
      pendingDescriptions: safeCount(response.summary?.pendingDescriptions),
      blockingIssueCount: safeCount(response.summary?.blockingIssueCount),
      warningCount: safeCount(response.summary?.warningCount),
      shortageCount: safeCount(response.summary?.shortageCount),
      canContinue: response.summary?.canContinue === true,
    },
  };
}

export function normalizeSalesImportRegularizationPlan(response = {}) {
  return {
    draftId: safeId(response.draftId),
    draftVersion: safeId(response.draftVersion),
    previewRevision: safeId(response.previewRevision),
    planVersion: safeId(response.planVersion),
    confirmedAt: response.confirmedAt || null,
    regularizations: Array.isArray(response.regularizations)
      ? response.regularizations
      : [],
    alreadyConfirmed: response.alreadyConfirmed === true,
    inventoryMutated: response.inventoryMutated === true,
    movementsCreated: response.movementsCreated === true,
  };
}

export function validateUnitsPerSale(value) {
  const text = String(value ?? "").trim();
  if (!UNITS_PER_SALE_PATTERN.test(text) || !Number.isFinite(Number(text)) || Number(text) <= 0) {
    return "Las unidades por venta deben ser un número positivo con hasta 10 decimales.";
  }
  return "";
}

export function formatUnitsPerSaleInput(value) {
  const text = String(value ?? "").trim();
  if (!/^\d+(?:\.\d+)?$/.test(text)) return text;
  const [whole, fraction] = text.split(".");
  if (fraction === undefined) return whole;
  const significantFraction = fraction.replace(/0+$/, "");
  return significantFraction ? `${whole}.${significantFraction}` : whole;
}

export function orderSalesImportDescriptions(descriptions = [], stableOrder = []) {
  const stablePositions = new Map(
    stableOrder.map((normalizedDescription, index) => [normalizedDescription, index])
  );

  return descriptions
    .map((description, index) => ({ description, index }))
    .sort((left, right) => {
      const leftStablePosition = stablePositions.get(left.description.normalizedDescription);
      const rightStablePosition = stablePositions.get(right.description.normalizedDescription);
      const leftHasStablePosition = leftStablePosition !== undefined;
      const rightHasStablePosition = rightStablePosition !== undefined;

      if (leftHasStablePosition && rightHasStablePosition) {
        return leftStablePosition - rightStablePosition;
      }
      if (leftHasStablePosition !== rightHasStablePosition) {
        return leftHasStablePosition ? -1 : 1;
      }

      const leftPending = left.description.resolution.kind === "PENDING" ? 0 : 1;
      const rightPending = right.description.resolution.kind === "PENDING" ? 0 : 1;
      return leftPending - rightPending || left.index - right.index;
    })
    .map(({ description }) => description);
}

export function canContinueSalesImportStepTwo(preview, plan) {
  if (!preview?.draftId || !preview?.version || !preview?.previewRevision) return false;
  if (!preview.summary.canContinue || preview.summary.pendingDescriptions > 0) return false;
  if (preview.summary.blockingIssueCount > 0) return false;
  if (preview.shortages.length === 0) return true;
  return Boolean(
    plan?.draftId === preview.draftId &&
    plan?.draftVersion === preview.version &&
    plan?.previewRevision === preview.previewRevision &&
    plan?.confirmedAt
  );
}
